// pages/RolesPage.jsx

import React, { useMemo, useState } from "react";
import {
  Shield,
  Plus,
  Search,
  Edit,
  KeyRound,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import { DataTable } from "@/components/ui/table";
import TableText from "@/components/common/TableText";
import { useNavigate } from "react-router-dom";

const RolesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // DUMMY DATA
  const roles = [
    {
      id: 1,
      role_name: "Super Admin",
      description: "Full access to all modules and settings",
      permissions: [
        "Manage Users",
        "Manage Roles",
        "Billing",
        "Analytics",
        "Company Setup",
        "System Settings",
      ],
      total_users: 2,
      is_system_role: true,
      is_active: true,
      created_by: "system",
    },

    {
      id: 2,
      role_name: "Operations Manager",
      description: "Manage shipment and operations workflow",
      permissions: [
        "Track Containers",
        "Manage Bookings",
        "Invoices",
        "Reports",
      ],
      total_users: 12,
      is_system_role: false,
      is_active: true,
      created_by: "super_admin",
    },

    {
      id: 3,
      role_name: "Accountant",
      description: "Access accounting and financial reports",
      permissions: ["Invoices", "Expenses", "Profit Reports", "Tax Reports"],
      total_users: 5,
      is_system_role: false,
      is_active: true,
      created_by: "admin",
    },

    {
      id: 4,
      role_name: "Data Entry",
      description: "Limited access for data management",
      permissions: ["Create Entries", "Edit Entries"],
      total_users: 18,
      is_system_role: false,
      is_active: false,
      created_by: "admin",
    },
  ];

  // FILTERED DATA
  const filteredRoles = useMemo(() => {
    return roles.filter((role) => {
      const search = searchTerm.toLowerCase();

      return (
        role.role_name?.toLowerCase().includes(search) ||
        role.description?.toLowerCase().includes(search)
      );
    });
  }, [roles, searchTerm]);

  const handleViewRolePermissions = (role) => {
    navigate(`/admin/roles/${role.id}/permissions`, {
      state: { role },
    });
  };

  // TABLE COLUMNS
  const columns = [
    {
      key: "role",
      label: "Role",
      thClass: "min-w-[280px]",

      render: (row) => (
        <div
          onClick={() => handleViewRolePermissions(row)}
          className="
      group
      flex items-start gap-3
      cursor-pointer
      rounded-2xl
      p-2
      -m-2
      transition-all duration-200
      hover:bg-slate-50
      dark:hover:bg-slate-800/60
    "
        >
          {/* ICON */}
          <div
            className="
        flex h-11 w-11 shrink-0 items-center justify-center
        rounded-2xl
        bg-violet-500/10
        text-violet-600
        transition-all duration-200
        group-hover:scale-105
        group-hover:bg-violet-500/20
        dark:bg-violet-500/20
        dark:text-violet-400
      "
          >
            <Shield className="h-5 w-5" />
          </div>

          {/* INFO */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p
                className="
            truncate text-sm font-semibold
            text-slate-900
            transition-colors
            group-hover:text-violet-600
            dark:text-white
            dark:group-hover:text-violet-400
          "
              >
                {row.role_name}
              </p>

              {row.is_system_role && (
                <span
                  className="
              rounded-full
              bg-slate-100 px-2 py-0.5
              text-[10px] font-semibold
              text-slate-600
              dark:bg-slate-700
              dark:text-slate-300
            "
                >
                  SYSTEM
                </span>
              )}
            </div>

            <p
              className="
          mt-0.5 line-clamp-2
          text-xs text-slate-500
          dark:text-slate-400
        "
            >
              {row.description}
            </p>
          </div>
        </div>
      ),
    },

    {
      key: "permissions",
      label: "Permissions",
      thClass: "min-w-[320px]",

      render: (row) => (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5 text-slate-400" />

            <span className="text-xs text-slate-500 dark:text-slate-400">
              {row.permissions.length} permissions
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {row.permissions.slice(0, 4).map((permission) => (
              <span
                key={permission}
                className="
                  rounded-full
                  bg-slate-100 px-2.5 py-1
                  text-[11px] font-medium
                  text-slate-700
                  dark:bg-slate-800
                  dark:text-slate-300
                "
              >
                {permission}
              </span>
            ))}

            {row.permissions.length > 4 && (
              <span
                className="
                  rounded-full
                  bg-teal-500/10 px-2.5 py-1
                  text-[11px] font-semibold
                  text-teal-600
                  dark:text-teal-400
                "
              >
                +{row.permissions.length - 4} more
              </span>
            )}
          </div>
        </div>
      ),
    },

    {
      key: "users",
      label: "Assigned Users",

      render: (row) => (
        <TableText
          title={row.total_users}
          subtitle="Users Assigned"
          titleClassName="
            font-semibold
            text-slate-900
            dark:text-white
          "
        />
      ),
    },

    {
      key: "createdBy",
      label: "Created By",

      render: (row) => (
        <TableText
          title={row.created_by || "-"}
          subtitle={`Role ID: ${row.id}`}
        />
      ),
    },

    {
      key: "status",
      label: "Status",

      render: (row) => (
        <span
          className={`
            inline-flex items-center gap-1.5
            rounded-full px-2.5 py-1
            text-xs font-semibold

            ${
              row.is_active
                ? `
                  bg-emerald-50
                  text-emerald-700
                  dark:bg-emerald-500/10
                  dark:text-emerald-400
                `
                : `
                  bg-red-50
                  text-red-700
                  dark:bg-red-500/10
                  dark:text-red-400
                `
            }
          `}
        >
          {row.is_active ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <XCircle className="h-3.5 w-3.5" />
          )}

          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      key: "actions",
      label: "Actions",
      thClass: "text-right pr-4",
      tdClass: "pr-4",

      render: () => (
        <div className="flex items-center justify-end gap-2">
          <button
            className="
              rounded-xl p-2
              text-slate-500
              transition-colors
              hover:bg-slate-100
              hover:text-teal-600
              dark:hover:bg-slate-800
              dark:hover:text-teal-400
            "
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-100 p-6 dark:bg-slate-900">
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1
              className="
                flex items-center gap-2
                text-2xl font-bold
                text-slate-900
                dark:text-white
              "
            >
              <Shield className="h-7 w-7 text-violet-500" />
              Roles & Permissions
            </h1>

            <p className="mt-1 text-slate-500 dark:text-slate-400">
              Manage all roles and access permissions
            </p>
          </div>

          <button
            className="
              inline-flex items-center gap-2
              rounded-xl
              bg-teal-500 px-4 py-2.5
              text-sm font-medium text-white
              transition-colors
              hover:bg-teal-600
            "
          >
            <Plus className="h-4 w-4" />
            Add Role
          </button>
        </div>

        {/* SEARCH */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-96">
            <Search
              className="
                absolute left-3 top-1/2
                h-4 w-4 -translate-y-1/2
                text-slate-400
              "
            />

            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-900 dark:text-white">
              {filteredRoles.length}
            </span>{" "}
            roles found
          </div>
        </div>

        {/* TABLE */}
        <div
          className="
            overflow-hidden rounded-2xl
            border border-slate-200
            bg-white
            dark:border-slate-800
            dark:bg-slate-900
          "
        >
          <DataTable
            data={filteredRoles}
            columns={columns}
            loading={false}
            emptyText="No roles found"
            Icon={Shield}
          />
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
